/**
 * ChainArrow
 * ──────────
 * Replaces a plain `<ArrowRight />` between two Learning Impact Chain
 * tiles. Visualises the "discount step" — the value lost (or gained)
 * between consecutive stages of the chain.
 *
 * Per the Missing Middle brief: the chain should communicate "Value is
 * flowing through the organisation. Value can accelerate. Value can
 * leak. Mission Control shows where this is happening." This arrow
 * makes the *between-stage* drop visible at a glance:
 *
 *   • Drop > 20 pts → rose (a real leak — eyeball lands here)
 *   • Drop 10–20    → amber
 *   • Drop  0–10    → slate (mild discount, normal funnel)
 *   • Gain  > 0     → emerald (Maturity or ROI multiplier lifts value)
 *
 * `fromScore` and `toScore` are 0–100 normalised health scores (the
 * caller does the normalisation; ROI multiplier ≠ health %).
 *
 * The "worst" arrow in a chain can be flagged with `pulse` so the eye
 * jumps to the largest leak — the same point the "Largest Value Leak"
 * panel calls out below.
 */
import React from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export default function ChainArrow({
  fromScore,
  toScore,
  pulse = false,
  testId,
  expected = null,
}) {
  // Hide the discount label entirely until both scores are known.
  // Showing "−0 pts" against an empty stage is noise, not signal.
  const hasBoth = Number.isFinite(fromScore) && Number.isFinite(toScore);
  const drop = hasBoth ? Math.round(fromScore - toScore) : null;

  // Transfer efficiency — how much of the prior stage's value carries
  // through to the next (toScore / fromScore). <100% = erosion,
  // >100% = amplification. This is the small "% impact" label shown
  // above the arrow so leaders can read the scale of each hand-off.
  const efficiency = hasBoth && fromScore > 0
    ? Math.round((toScore / fromScore) * 100)
    : null;
  let effTone = 'slate';
  if (efficiency !== null) {
    if (efficiency < 80) effTone = 'rose';
    else if (efficiency < 95) effTone = 'amber';
    else if (efficiency > 105) effTone = 'emerald';
  }

  // Plan comparison — when a leader has set an expected transfer % for this
  // hand-off, flag when the live efficiency is materially below plan.
  const hasPlan = Number.isFinite(expected) && expected > 0 && efficiency !== null;
  const belowPlan = hasPlan && efficiency < expected;
  if (belowPlan) effTone = 'rose';

  // Tone selection: rose for real leaks, emerald for gains, otherwise
  // a quiet slate so non-leak arrows don't shout.
  let tone = 'slate';
  if (drop !== null) {
    if (drop > 20) tone = 'rose';
    else if (drop > 10) tone = 'amber';
    else if (drop < 0) tone = 'emerald';
  }

  const arrowCls = cn(
    'h-5 w-5 flex-shrink-0 transition-all duration-300',
    tone === 'rose' && 'text-rose-500',
    tone === 'amber' && 'text-amber-500',
    tone === 'emerald' && 'text-emerald-400',
    tone === 'slate' && 'text-slate-500',
    pulse && tone !== 'slate' && 'animate-pulse',
  );

  // Aligned to the vertical centre of the circular chain nodes
  // (h-14 circle). A tiny transfer-efficiency % sits above the arrow
  // (absolutely positioned so it never disturbs the chain row width),
  // giving the scale of each stage's amplification/erosion at a glance.
  return (
    <div
      className="relative flex items-center flex-shrink-0 mt-[18px]"
      data-testid={testId}
      title={
        efficiency === null
          ? (drop === null ? undefined
            : drop > 0 ? `Value drops ${drop} pts between these stages`
              : drop < 0 ? `Value lifts ${Math.abs(drop)} pts between these stages`
                : 'No change between these stages')
          : `${efficiency}% of value carries through — ${
            efficiency < 100 ? `${100 - efficiency}% erosion`
              : efficiency > 100 ? `${efficiency - 100}% amplification`
                : 'no change'} between these stages${
            hasPlan ? ` · plan ${expected}% (${belowPlan ? `${expected - efficiency} below plan` : 'meets plan'})` : ''}`
      }
    >
      {efficiency !== null && (
        <span
          className={cn(
            'absolute left-1/2 -translate-x-1/2 -top-[15px] text-[10px] font-bold leading-none whitespace-nowrap tabular-nums select-none',
            effTone === 'rose' && 'text-rose-400',
            effTone === 'amber' && 'text-amber-400',
            effTone === 'emerald' && 'text-emerald-400',
            effTone === 'slate' && 'text-slate-500',
          )}
          data-testid={testId ? `${testId}-efficiency` : undefined}
        >
          {efficiency}%
          {belowPlan && <span className="ml-0.5" data-testid={testId ? `${testId}-below-plan` : undefined}>▼</span>}
        </span>
      )}
      <ArrowRight className={arrowCls} />
    </div>
  );
}
